import { keyframes } from '@emotion/react'
import { Alert, AlertTitle, alpha, Box, type SxProps, type Theme, Typography } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import React, { type FC, useEffect, useRef } from 'react'

import { assert } from '../../../../utils'

import { ConnectwareError, ConnectwareErrorType, NotificationSeverity, type ToastAction, Translation } from '../../../../domain'

import { useTranslator } from '../../Internationalization'
import { Button, IconButton } from '../Button'
import { type AlertRamp, notificationBorderColor, notificationCloseIconColor } from '../../theme/alert'
import { CloseIcon } from '../../theme/icons'
import { notificationIconMapping, toAlertColor } from './Severity'

export type { ToastAction }

export type ToastProps = Readonly<{
    severity: NotificationSeverity
    title: string
    description?: string
    action?: ToastAction
    /** Omit to render without a close button - the grouped/counter toast has none per Figma. */
    onClose?: () => void
    /**
     * Called when `autoHideDurationMs` elapses. Defaults to `onClose` when omitted - most toasts
     * dismiss the same way whether the user clicks close or the timer runs out. Pass this explicitly
     * for a toast that auto-dismisses but has no close button (the grouped/counter toast).
     */
    onAutoHide?: () => void
    /**
     * Both the real auto-dismiss delay and the duration the visible countdown bar animates over -
     * the two are driven by the same timer (including pausing together on hover), so they can never
     * drift apart. Omit when the toast has no auto-dismiss (a persisted toast).
     */
    autoHideDurationMs?: number
    /**
     * When set to more than 1, replaces the severity icon with a solid counter circle (Figma) for
     * aggregated, repeated-event toasts - the icon and the counter never show together.
     */
    count?: number
}>

/** notistack's own default bottom-right container offsets, read from its source - shared so anything positioning relative to the general Toast zone has one source of truth. */
export const notistackDefaultBottomOffsetPx = 14
export const notistackDefaultRightOffsetPx = 20

/**
 * Real auto-dismiss timing per severity (lore/engineering/CC-4102-notifications/Notifications_Migration.plan.md's Toast behavior spec).
 * Passed to `Toast` as `autoHideDurationMs`, which owns the actual dismiss timer as well as the
 * countdown bar - notistack's own `autoHideDuration` is deliberately left unset (`ToastPortal`) so it
 * never races this.
 */
export const notificationToastAutoHideDurationMs: Readonly<Record<NotificationSeverity, number>> = {
    [NotificationSeverity.INFO]: 4_000,
    [NotificationSeverity.SUCCESS]: 4_000,
    [NotificationSeverity.WARNING]: 8_000,
    [NotificationSeverity.ERROR]: 8_000,
}

const TOAST_WIDTH = 320

const containerStyle: SxProps<Theme> = {
    width: TOAST_WIDTH,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 1,
    '&:hover .notification-toast-timer-fill': { animationPlayState: 'paused' },
}

const alertStyle: SxProps<Theme> = { alignItems: 'center' }

const actionsStyle: SxProps<Theme> = { alignItems: 'center', display: 'flex', gap: 0.5 }

const counterBadgeStyle = (severity: NotificationSeverity) => (theme: Theme) => ({
    alignItems: 'center',
    bgcolor: theme.palette[toAlertColor[severity]][600],
    borderRadius: '50%',
    color: theme.palette.common.white,
    display: 'flex',
    fontSize: 10,
    fontWeight: 700,
    height: 24,
    justifyContent: 'center',
    lineHeight: 1,
    width: 24,
})

/** Structurally identical to `primaryOutline`/`dangerOutline` - reused as-is where the colors already match (info, error). */
const actionButtonVariant: Readonly<Record<AlertRamp, 'primaryOutline' | 'dangerOutline'>> = {
    error: 'dangerOutline',
    info: 'primaryOutline',
    success: 'primaryOutline',
    warning: 'primaryOutline',
}

/**
 * `primaryOutline`/`dangerOutline` are colored for info/error, so those two need no override here.
 * warning's colors are Figma-confirmed (border/700-text); success has no Figma reference yet, so it
 * mirrors the same border/200 + text/700 pattern as an assumption, flagged for confirmation. Hover/
 * active states aren't shown in either Figma export either - mirrored from `primaryOutline`'s own
 * default/hover/active progression (100/200/400 steps) rather than left uncovered.
 */
const actionButtonSeverityStyle: Partial<Record<AlertRamp, (theme: Theme) => SystemStyleObject<Theme>>> = {
    warning: (theme) => ({
        color: theme.palette.warning[700],
        borderColor: theme.palette.warning[200],
        '&:hover': { backgroundColor: theme.palette.warning[100] },
        '&:active': { color: theme.palette.warning[800], backgroundColor: theme.palette.warning[200], borderColor: theme.palette.warning[400] },
    }),
    success: (theme) => ({
        color: theme.palette.success[700],
        borderColor: theme.palette.success[200],
        '&:hover': { backgroundColor: theme.palette.success[100] },
        '&:active': { color: theme.palette.success[800], backgroundColor: theme.palette.success[200], borderColor: theme.palette.success[400] },
    }),
}

const timerTrackLayoutStyle = { bottom: 0, height: 3, left: 0, position: 'absolute', right: 0 } as const

const timerTrackStyle = (severity: NotificationSeverity) => (theme: Theme) => {
    const borderColor = notificationBorderColor[toAlertColor[severity]](theme)
    assert(borderColor, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Theme is missing a notification border color', { severity }))

    return { ...timerTrackLayoutStyle, bgcolor: alpha(borderColor, 0.4) }
}

const closeIconStyle = (severity: NotificationSeverity) => (theme: Theme) => {
    const color = notificationCloseIconColor[toAlertColor[severity]](theme)
    assert(color, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Theme is missing a notification close icon color', { severity }))

    return { color }
}

const shrinkFromFullWidth = keyframes`
    from { transform: scaleX(1); }
    to { transform: scaleX(0); }
`

/**
 * Owns the single timer that both actually dismisses the toast and drives the countdown bar's
 * animation duration, so the two can't drift apart - previously the bar was purely CSS (paused via
 * `:hover`) while the real dismiss was a separate, caller-owned `setTimeout` that never paused on
 * hover, so a hovered toast could vanish out from under the user even while its bar looked frozen.
 * Pauses/resumes on mouseenter/mouseleave of the same element the CSS `:hover` rule targets, using
 * refs (not state) so hovering never triggers a re-render.
 */
const usePausableAutoHide = (
    autoHideDurationMs: number | undefined,
    onElapsed: (() => void) | undefined,
    resetKey: unknown
): Readonly<{ onMouseEnter: () => void, onMouseLeave: () => void }> => {
    const isHoveredRef = useRef(false)
    const remainingMsRef = useRef(0)
    const startedAtRef = useRef(0)
    const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>()
    const onElapsedRef = useRef(onElapsed)

    useEffect(() => {
        onElapsedRef.current = onElapsed
    })

    useEffect(() => {
        if (autoHideDurationMs === undefined) {
            return
        }

        assert(onElapsedRef.current, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Toast has autoHideDurationMs but nothing to call when it elapses'))

        remainingMsRef.current = autoHideDurationMs

        if (!isHoveredRef.current) {
            startedAtRef.current = Date.now()
            timeoutIdRef.current = setTimeout(() => onElapsedRef.current?.(), remainingMsRef.current)
        }

        return () => clearTimeout(timeoutIdRef.current)
        // `resetKey` (e.g. an aggregated toast's count) is the deliberate reset trigger, not `onElapsed` - see `onElapsedRef` above
    }, [autoHideDurationMs, resetKey])

    return {
        onMouseEnter: (): void => {
            isHoveredRef.current = true

            if (autoHideDurationMs === undefined || timeoutIdRef.current === undefined) {
                return
            }

            clearTimeout(timeoutIdRef.current)
            timeoutIdRef.current = undefined
            remainingMsRef.current -= Date.now() - startedAtRef.current
        },
        onMouseLeave: (): void => {
            isHoveredRef.current = false

            if (autoHideDurationMs === undefined) {
                return
            }

            /** Guards against a stray double `mouseleave` (e.g. the hovered element being removed/replaced under the cursor, like the timer-fill `Box` remounting via its own `key={count}` on an aggregated repeat) scheduling two timeouts for the same remaining duration - clear any already-running one first. */
            clearTimeout(timeoutIdRef.current)
            startedAtRef.current = Date.now()
            timeoutIdRef.current = setTimeout(() => onElapsedRef.current?.(), remainingMsRef.current)
        },
    }
}

export const Toast: FC<ToastProps> = ({ severity, title, description, action, onClose, onAutoHide, autoHideDurationMs, count }) => {
    const translator = useTranslator()
    const hoverHandlers = usePausableAutoHide(autoHideDurationMs, onAutoHide ?? onClose, count)

    return (
        <Box data-testid="notification-toast" data-severity={severity} sx={containerStyle} {...hoverHandlers}>
            <Alert
                severity={toAlertColor[severity]}
                iconMapping={notificationIconMapping}
                icon={count && count > 1
                    ? (
                        <Box data-testid="notification-toast-count" sx={counterBadgeStyle(severity)}>
                            {translator.formatTranslation(Translation.NOTIFICATION_TOAST_COUNT, { count })}
                        </Box>
                    )
                    : undefined}
                variant="notification"
                sx={alertStyle}
                action={(action ?? onClose) && (
                    <Box sx={actionsStyle}>
                        {action && (
                            <Button
                                data-testid="notification-toast-action"
                                variant={actionButtonVariant[toAlertColor[severity]]}
                                sx={actionButtonSeverityStyle[toAlertColor[severity]]}
                                size="small"
                                onClick={action.onClick}
                            >
                                {action.label}
                            </Button>
                        )}
                        {onClose && (
                            <IconButton
                                data-testid="notification-toast-close"
                                aria-label={translator.formatTranslation(Translation.CLOSE)}
                                variant="ghost"
                                size="small"
                                onClick={onClose}
                            >
                                <CloseIcon fontSize="small" sx={closeIconStyle(severity)} />
                            </IconButton>
                        )}
                    </Box>
                )}
            >
                <AlertTitle>{title}</AlertTitle>
                {description && <Typography variant="body2">{description}</Typography>}
            </Alert>
            {typeof autoHideDurationMs === 'number' && (
                <Box aria-hidden="true" data-testid="notification-toast-timer" sx={timerTrackStyle(severity)}>
                    {/* `key={count}` remounts the fill on each aggregated repeat, restarting its CSS animation from full width - the parent keeps the same underlying toast mounted throughout, and `usePausableAutoHide` above resets the real timer on the same `count` change, so the visible bar and the actual dismiss restart together. */}
                    <Box
                        key={count}
                        className="notification-toast-timer-fill"
                        data-testid="notification-toast-timer-fill"
                        sx={(theme) => ({
                            animation: `${shrinkFromFullWidth} linear forwards`,
                            animationDuration: `${autoHideDurationMs}ms`,
                            bgcolor: theme.palette[toAlertColor[severity]].main,
                            height: '100%',
                            transformOrigin: 'left',
                            width: '100%',
                            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                        })}
                    />
                </Box>
            )}
        </Box>
    )
}
